import React, {createElement} from 'react'

import invariant from 'invariant'
import {TransitionGroup} from 'react-transition-group'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {closeModal} from '../../redux/features/modal'

import {ModalConfig, ModalConfigOpened, ModalContentType} from '../../types'

import ModalPortal from '../../../../commons/src/components/modal-portal'

import ApiKeyModal from './api-key'
import AuthChangePasswordModal from './auth-change-password'
import ConfirmDeleteModal from './confirm-delete'
import ConfirmDeleteMessageModal from './confirm-delete-message'
import ConfirmToggleAvailabilityModal from './confirm-toggle-availability'
import ContactModal from './contact'
import ContactFormModal from './contact-form'
import CreateDynamicProductionTimeModal from './dynamic-production-times/create'
import EditDiscountsModal from './edit-discounts'
import FatalErrorModal from './fatal-error'
import FinishGroupModal from './finish-group'
import CreateInfillModal from './infills/create'
import EditInfillsModal from './infills/edit'
import LoadingModal from './loading'
import MaterialModal from './material'
import AddTrackingModal from './orders/add-tracking'
import AdditionalCostModal from './orders/additional-cost'
import PartialRefundDetails from './orders/partial-refund-details'
import RequestRefundModal from './orders/request-refund'
import CreatePricingModal from './pricings/create'
import CreateProductionTimeModal from './production-times/create'
import EditProductionTimesModal from './production-times/edit'
import EditProviderEmailsModal from './provider-email/edit'
import AddProviderInfoDimensionsModal from './provider-info/add-dimensions'
import AddProviderInfoDynamicProductionTimeModal from './provider-info/add-dynamic-production-time'
import AddProviderInfoPricingModal from './provider-info/add-pricing'
import AddProviderInfoPrintingMethodModal from './provider-info/add-printing-method'
import AddProviderInfoProductionTimesModal from './provider-info/add-production-times'
import SuggestImmediateEditingModal from './provider-info/suggest-immediate-editing'
import CreateShippingsModal from './shippings/create'
import EditShippingsModal from './shippings/edit'
import SimplteConfirmModal from './simple-confirm'
import SimpleContentModal from './simple-content'
import CreateTolerancesModal from './tolerances/create'
import EditTolerancesModal from './tolerances/edit'

const modals: Record<
  ModalContentType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {component: React.ComponentType<any>; portalProps?: ModalConfigOpened}
> = {
  [ModalContentType.FATAL_ERROR]: {component: FatalErrorModal},
  [ModalContentType.AUTH_CHANGE_PASSWORD]: {component: AuthChangePasswordModal},
  [ModalContentType.MATERIAL]: {component: MaterialModal},
  [ModalContentType.FINISH_GROUP]: {component: FinishGroupModal},
  [ModalContentType.CREATE_PRICING]: {component: CreatePricingModal},
  [ModalContentType.CREATE_DYNAMIC_PRODUCTION_TIME]: {component: CreateDynamicProductionTimeModal},
  [ModalContentType.EDIT_DISCOUNTS]: {component: EditDiscountsModal},
  [ModalContentType.CREATE_SHIPPINGS]: {component: CreateShippingsModal},
  [ModalContentType.EDIT_SHIPPINGS]: {component: EditShippingsModal},
  [ModalContentType.CREATE_INFILL]: {component: CreateInfillModal},
  [ModalContentType.EDIT_INFILLS]: {component: EditInfillsModal},
  [ModalContentType.CREATE_TOLERANCE]: {component: CreateTolerancesModal},
  [ModalContentType.EDIT_TOLERANCES]: {component: EditTolerancesModal},
  [ModalContentType.EDIT_PROVIDER_EMAILS]: {component: EditProviderEmailsModal},
  [ModalContentType.CREATE_PRODUCTION_TIME]: {component: CreateProductionTimeModal},
  [ModalContentType.EDIT_PRODUCTION_TIMES]: {component: EditProductionTimesModal},
  [ModalContentType.CONFIRM_DELETE]: {component: ConfirmDeleteModal},
  [ModalContentType.CONFIRM_TOGGLE_AVAILABILITY]: {component: ConfirmToggleAvailabilityModal},
  [ModalContentType.ADD_PROVIDER_INFO_DIMENSIONS]: {component: AddProviderInfoDimensionsModal},
  [ModalContentType.ADD_PROVIDER_INFO_PRODUCTION_TIMES]: {
    component: AddProviderInfoProductionTimesModal,
  },
  [ModalContentType.ADD_PROVIDER_INFO_PRICING]: {component: AddProviderInfoPricingModal},
  [ModalContentType.ADD_PROVIDER_INFO_DYNAMIC_PRODUCTION_TIME]: {
    component: AddProviderInfoDynamicProductionTimeModal,
  },
  [ModalContentType.ADD_PROVIDER_INFO_PRINTING_METHOD]: {
    component: AddProviderInfoPrintingMethodModal,
  },
  [ModalContentType.PROVIDER_INFO_SUGGEST_IMMEDIATE_EDITING]: {
    component: SuggestImmediateEditingModal,
  },
  [ModalContentType.CONTACT]: {component: ContactModal},
  [ModalContentType.CONTACT_FORM]: {component: ContactFormModal},
  [ModalContentType.CONFIRM_DELETE_MESSAGE]: {component: ConfirmDeleteMessageModal},
  [ModalContentType.SIMPLE_CONTENT]: {component: SimpleContentModal},
  [ModalContentType.SIMPLE_CONFIRM]: {component: SimplteConfirmModal},
  [ModalContentType.ADDITIONAL_COST]: {component: AdditionalCostModal},
  [ModalContentType.LOADING]: {component: LoadingModal},
  [ModalContentType.ENFORCE_TRACKING]: {component: AddTrackingModal},
  [ModalContentType.REQUEST_REFUND]: {component: RequestRefundModal},
  [ModalContentType.PARTIAL_REFUND_DETAILS]: {component: PartialRefundDetails},
  [ModalContentType.API_KEY]: {component: ApiKeyModal},
}

const getContent = (modalConfig: ModalConfig, close: () => void) => {
  if (modalConfig === null) {
    return <div />
  }

  const {contentType, contentProps, isCloseable} = modalConfig

  invariant(contentType in modals, `Unknown modal contentType "${contentType}"`)

  const modal = modals[contentType]

  return (
    <ModalPortal
      {...(modal.portalProps || {})}
      key={contentType}
      onClose={isCloseable ? () => close() : undefined}
    >
      {createElement(modal.component, {
        ...contentProps,
        meta: {isCloseable, ...modal.portalProps},
      })}
    </ModalPortal>
  )
}

const Modal = () => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(state => state.modal.isOpen)
  const modalConfig = useAppSelector(state => state.modal.modalConfig)

  const close = () => dispatch(closeModal())

  return <TransitionGroup>{isOpen && getContent(modalConfig, close)}</TransitionGroup>
}

export default Modal
